import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import { useAdminUserSession } from '../../../../models/hooks/adminStateHooks';
import { usePricebookAddOnsForUpgradeQuery } from '../../../../queries/Admin/Pricebook';
import { AdminPermission } from '../../../../viewmodels/AdminViewModels';
import {
	useExecuteUpgradeMutation,
	useNextInvoicePreviewMutation,
	usePreviewUpgradeMutation,
	useProformaInvoiceUrlMutation,
	useSubscriptionStateQuery,
} from '../../../../queries/Admin/UpgradeWizard';
import { Button } from '../../../../web/components-v2/Button';
import { Checkbox } from '../../../../web/components-v2/Checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../web/components-v2/Select';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { Modal } from '../../../../web/components/Modal';
import {
	GenerateUpgradeProposalModal,
	UpgradeProposalPrefillData,
} from '../PricingWizard/GenerateUpgradeProposalModal';
import { GenerateContractModal } from '../PricingWizard/GenerateContractModal';
import type { CurrentClientContractPrefillData } from '../PricingWizard/GenerateContractModal/types';
import { PricebookAddOn } from '../PricingWizard/types';
import { styleSheet } from './styles';
import {
	BillingCycleOption,
	getAvailableUpgradeComponents,
	UpgradeComponentRequest,
	UpgradePreviewResponse,
} from './types';

// Add-on categories for grouping
type AddOnCategory =
	| 'website'
	| 'additionalUsers'
	| 'googleLsa'
	| 'googlePpc'
	| 'metaAds'
	| 'handwrittenCards'
	| 'other';

function categorizeAddOn(addOn: PricebookAddOn): AddOnCategory {
	const name = addOn.name.toLowerCase();
	if (name.includes('website')) return 'website';
	if (name.includes('user')) return 'additionalUsers';
	// PPC must be checked before googleLsa — "Google PPC" contains "google" and would otherwise be miscategorized.
	if (name.includes('ppc') || name.includes('pay-per-click')) return 'googlePpc';
	if (name.includes('google') || name.includes('lsa')) return 'googleLsa';
	if (name.includes('meta') || name.includes('facebook')) return 'metaAds';
	if (name.includes('handwritten')) return 'handwrittenCards';
	return 'other';
}

const CATEGORY_LABELS: Record<AddOnCategory, string> = {
	website: 'Website',
	additionalUsers: 'Additional Users',
	googleLsa: 'Google LSA',
	googlePpc: 'Google PPC',
	metaAds: 'Meta Ads Premium',
	handwrittenCards: 'Handwritten Cards',
	other: 'Other Add-ons',
};

// Order for displaying categories
const CATEGORY_ORDER: AddOnCategory[] = [
	'website',
	'additionalUsers',
	'googleLsa',
	'googlePpc',
	'metaAds',
	'handwrittenCards',
	'other',
];

interface IProps {
	className?: string;
	styles?: StyleDeclarationValue[];
	accountId: string;
	billingReferenceId?: number | null;
	companyName?: string;
	customContentEnabled?: boolean;
}

export function UpgradeWizard({
	className,
	styles: customStyles,
	accountId,
	billingReferenceId,
	companyName,
	customContentEnabled,
}: IProps) {
	const userSession = useAdminUserSession();
	// Check permissions - Success team has ModifyAccount, Sales team does not
	const isSuccessTeam = userSession.hasPermission(AdminPermission.ModifyAccount);

	// API queries
	const subscriptionQuery = useSubscriptionStateQuery({ accountId, enabled: !!accountId && !!billingReferenceId });
	const addOnsQuery = usePricebookAddOnsForUpgradeQuery();
	const previewMutation = usePreviewUpgradeMutation();
	const executeMutation = useExecuteUpgradeMutation();
	const proformaInvoiceMutation = useProformaInvoiceUrlMutation();
	const nextInvoiceMutation = useNextInvoicePreviewMutation();

	// Form state - track selected add-ons by their ID
	const [selectedAddOnIds, setSelectedAddOnIds] = React.useState<Set<string>>(new Set());
	const [additionalUsersQty, setAdditionalUsersQty] = React.useState(1);

	// Billing cycle change state
	const [billingCycle, setBillingCycle] = React.useState<BillingCycleOption>(null);
	const [isSplitBilling, setIsSplitBilling] = React.useState(false);

	// Discount modal state
	const [showDiscountModal, setShowDiscountModal] = React.useState(false);
	const [discountMonths, setDiscountMonths] = React.useState<0 | 1 | 2 | 3>(0);
	const [pendingPreviewForDiscount, setPendingPreviewForDiscount] = React.useState<{
		subscriptionId: number;
		accountId: string;
		components: UpgradeComponentRequest[];
		newBillingCycle: BillingCycleOption;
	} | null>(null);

	// Preview results state
	const [previewResult, setPreviewResult] = React.useState<UpgradePreviewResponse | null>(null);

	// Modal state
	const [isConfirmModalOpen, setIsConfirmModalOpen] = React.useState(false);
	const [isSuccessModalOpen, setIsSuccessModalOpen] = React.useState(false);
	const [chargedAmount, setChargedAmount] = React.useState(0);
	const [executeWarnings, setExecuteWarnings] = React.useState<string[]>([]);
	const [serviceCreditApplied, setServiceCreditApplied] = React.useState(0);
	const [proformaInvoiceUrl, setProformaInvoiceUrl] = React.useState<string | null>(null);

	// Upgrade Proposal Modal state
	const [isUpgradeProposalModalOpen, setIsUpgradeProposalModalOpen] = React.useState(false);

	// Current Client Contract Modal state
	const [isCurrentClientContractOpen, setIsCurrentClientContractOpen] = React.useState(false);

	// Next Invoice Preview Modal state
	const [isNextInvoiceModalOpen, setIsNextInvoiceModalOpen] = React.useState(false);

	// Get the billing cycle - use selected billing cycle if changed, otherwise current subscription
	const effectiveBillingCycleMonths = React.useMemo(() => {
		if (billingCycle === 'Quarterly') return 3;
		if (billingCycle === 'Annual') return 12;
		return subscriptionQuery.data?.billingCycleMonths ?? 12;
	}, [billingCycle, subscriptionQuery.data?.billingCycleMonths]);

	// Filter add-ons by billing cycle and group by category
	const filteredAddOns = React.useMemo(() => {
		if (!addOnsQuery.data) return [];
		return addOnsQuery.data.filter(addOn => addOn.billingCycle === effectiveBillingCycleMonths);
	}, [addOnsQuery.data, effectiveBillingCycleMonths]);

	// Group add-ons by category
	const groupedAddOns = React.useMemo(() => {
		const groups: Record<AddOnCategory, PricebookAddOn[]> = {
			website: [],
			additionalUsers: [],
			googleLsa: [],
			googlePpc: [],
			metaAds: [],
			handwrittenCards: [],
			other: [],
		};

		filteredAddOns.forEach(addOn => {
			const category = categorizeAddOn(addOn);
			groups[category].push(addOn);
		});

		return groups;
	}, [filteredAddOns]);

	// Check which categories are already owned based on existing subscription components
	// Note: AdditionalUsers is excluded - users can always add more seats
	const ownedCategories = React.useMemo(() => {
		if (!subscriptionQuery.data?.existingComponents) return new Set<AddOnCategory>();

		const availableTypes = getAvailableUpgradeComponents(subscriptionQuery.data.existingComponents);
		const owned = new Set<AddOnCategory>();

		// If a type is NOT available, it means it's already owned
		// AdditionalUsers is NOT added here - users can always purchase more
		if (!availableTypes.includes('Website')) owned.add('website');
		if (!availableTypes.includes('GoogleLocalServiceAds')) owned.add('googleLsa');
		if (!availableTypes.includes('GooglePpc')) owned.add('googlePpc');
		if (!availableTypes.includes('MetaAds')) owned.add('metaAds');

		return owned;
	}, [subscriptionQuery.data?.existingComponents]);

	// Get existing components that are allocated (quantity > 0) for display
	const allocatedComponents = React.useMemo(() => {
		if (!subscriptionQuery.data?.existingComponents) return [];
		return subscriptionQuery.data.existingComponents.filter(c => c.allocatedQuantity > 0);
	}, [subscriptionQuery.data?.existingComponents]);

	// Get existing additional users count (to add to new quantity for total)
	const existingAdditionalUsersCount = React.useMemo(() => {
		const userComponent = allocatedComponents.find(c => c.name.toLowerCase().includes('user'));
		return userComponent?.allocatedQuantity ?? 0;
	}, [allocatedComponents]);

	// Determine available billing cycle options based on current cycle and eligibility
	const billingCycleOptions = React.useMemo(() => {
		const subData = subscriptionQuery.data;
		if (!subData?.isEligibleForBillingCycleChange) {
			return []; // No options if not eligible
		}

		const options: { value: BillingCycleOption; label: string }[] = [{ value: null, label: 'Keep Current' }];

		if (subData.billingCycleMonths === 12) {
			// Currently Annual - can switch to Quarterly
			options.push({ value: 'Quarterly', label: 'Quarterly' });
		} else if (subData.billingCycleMonths === 3) {
			// Currently Quarterly - can switch to Annual
			options.push({ value: 'Annual', label: 'Annual' });
		}

		return options;
	}, [subscriptionQuery.data]);

	// Get selected add-on objects
	const selectedAddOns = React.useMemo(() => {
		return filteredAddOns.filter(addOn => selectedAddOnIds.has(addOn.id));
	}, [filteredAddOns, selectedAddOnIds]);

	// Build prefill data from wizard state for the proposal modal
	const upgradeProposalPrefillData = React.useMemo<UpgradeProposalPrefillData>(() => {
		const sub = subscriptionQuery.data;
		if (!sub) return {};

		// Determine website upgrade from selected add-ons
		const websiteAddOn = selectedAddOns.find(a => categorizeAddOn(a) === 'website');
		let websiteUpgrade = '';
		if (websiteAddOn) {
			const name = websiteAddOn.name.toLowerCase();
			if (name.includes('essential')) websiteUpgrade = 'Essential Website';
			else if (name.includes('preferred')) websiteUpgrade = 'Preferred Website';
			else if (name.includes('prestige')) websiteUpgrade = 'Prestige Website';
		}

		// Calculate amount due today from preview result
		const amountDueToday = previewResult
			? previewResult.isImmediateBillingCycleChange
				? previewResult.amountDueToday
				: (previewResult.netTotal ?? previewResult.totalProratedCharge)
			: undefined;

		// Calculate months until renewal from days remaining
		const monthsUntilRenewal = sub.daysRemainingInPeriod ? String(Math.round(sub.daysRemainingInPeriod / 30)) : '';

		// Format renewal date as YYYY-MM-DD for date input
		const renewalDate = sub.currentPeriodEndsAt ? sub.currentPeriodEndsAt.split('T')[0] : '';

		// Determine billing cycle
		const billingCycleLabel = billingCycle ?? sub.billingCycleLabel;

		// Check selected add-ons for proposal add-on flags
		const hasMetaAds = selectedAddOns.some(a => categorizeAddOn(a) === 'metaAds');
		const hasGoogleLsa = selectedAddOns.some(a => categorizeAddOn(a) === 'googleLsa');

		return {
			websiteUpgrade,
			totalAmountDueToday: amountDueToday != null ? String(amountDueToday) : '',
			billingCycle: billingCycleLabel ?? 'Annual',
			monthsUntilRenewal,
			renewalDate,
			fbAdsChecked: false,
			fbPremiumChecked: hasMetaAds,
			gLSAChecked: hasGoogleLsa,
		};
	}, [subscriptionQuery.data, selectedAddOns, previewResult, billingCycle]);

	// Build prefill data for the Current Client Contract modal from subscription state and preview result
	const contractPrefillData = React.useMemo<CurrentClientContractPrefillData>(() => {
		const sub = subscriptionQuery.data;

		// Amount due today — same logic as upgradeProposalPrefillData
		const amountDueToday = previewResult
			? previewResult.isImmediateBillingCycleChange
				? previewResult.amountDueToday
				: (previewResult.netTotal ?? previewResult.totalProratedCharge)
			: undefined;

		const hasMetaAdsPremium = selectedAddOns.some(a => categorizeAddOn(a) === 'metaAds');
		const hasGoogleLsa = selectedAddOns.some(a => categorizeAddOn(a) === 'googleLsa');

		return {
			adminLink: `${window.location.origin}/admin/accounts/${accountId}`,
			companyName: companyName ?? '',
			currentLevitatePlan: '',
			renewalDate: sub?.currentPeriodEndsAt ? sub.currentPeriodEndsAt.split('T')[0] : '',
			billingCycle: billingCycle ?? sub?.billingCycleLabel ?? 'Annual',
			totalPriceDueToday: amountDueToday,
			fbPremiumChecked: hasMetaAdsPremium,
			gLSAChecked: hasGoogleLsa,
		};
	}, [subscriptionQuery.data, previewResult, billingCycle, companyName, accountId, selectedAddOns]);

	// Check if selected add-ons include discount-eligible components
	const hasDiscountEligibleAddOns = React.useMemo(() => {
		const eligible: AddOnCategory[] = ['website', 'googleLsa', 'googlePpc', 'metaAds'];
		return selectedAddOns.some(addOn => eligible.includes(categorizeAddOn(addOn)));
	}, [selectedAddOns]);

	// Show split billing if currently on Annual billing (even if switching to Quarterly)
	// Hide only if already on Quarterly billing
	const showSplitBilling = subscriptionQuery.data?.billingCycleMonths === 12;

	// Clear selections when billing cycle changes
	React.useEffect(() => {
		setSelectedAddOnIds(new Set());
		setPreviewResult(null);
		setIsSplitBilling(false);
	}, [effectiveBillingCycleMonths]);

	const handleAddOnToggle = (addOnId: string, checked: boolean) => {
		setSelectedAddOnIds(prev => {
			const newSet = new Set(prev);
			if (checked) {
				newSet.add(addOnId);
			} else {
				newSet.delete(addOnId);
			}
			return newSet;
		});
		setPreviewResult(null);
	};

	const handleBillingCycleChange = (value: string) => {
		// Convert empty string or 'null' back to null for 'Keep Current'
		setBillingCycle(value === '' || value === 'null' ? null : (value as BillingCycleOption));
		setPreviewResult(null);
	};

	// Execute the preview mutation with the given parameters
	const executePreview = (
		request: {
			subscriptionId: number;
			accountId: string;
			components: UpgradeComponentRequest[];
			newBillingCycle: BillingCycleOption;
		},
		discount: 0 | 1 | 2 | 3
	) => {
		previewMutation.mutate(
			{
				...request,
				useSplitBilling: isSplitBilling,
				discountMonths: discount,
			},
			{
				onSuccess: result => {
					setPreviewResult(result);
				},
				onError: error => {
					toast.error(`Failed to preview upgrade: ${error.message}`);
				},
			}
		);
	};

	const handlePreview = async (e: React.FormEvent) => {
		e.preventDefault();

		// Allow preview with just billing cycle change (no add-ons required)
		if (selectedAddOns.length === 0 && billingCycle === null) {
			toast.error('Please select at least one add-on or change the billing cycle');
			return;
		}

		if (!subscriptionQuery.data) {
			toast.error('No subscription data available');
			return;
		}

		// Build components array from selected add-ons
		const components = selectedAddOns.map(addOn => {
			const category = categorizeAddOn(addOn);
			// For additional users, send total (existing + new), not just the delta
			const quantity = category === 'additionalUsers' ? existingAdditionalUsersCount + additionalUsersQty : 1;
			return {
				componentId: addOn.componentId,
				pricePointId: addOn.pricePointId,
				quantity,
			};
		});

		const request = {
			subscriptionId: subscriptionQuery.data.subscriptionId,
			accountId,
			components,
			newBillingCycle: billingCycle,
		};

		// Check if any discount-eligible add-ons are selected
		if (hasDiscountEligibleAddOns) {
			// Show discount modal
			setPendingPreviewForDiscount(request);
			setShowDiscountModal(true);
			return;
		}

		// No discount-eligible add-ons, proceed directly with discountMonths: 0
		executePreview(request, 0);
	};

	const handleDiscountModalConfirm = () => {
		if (!pendingPreviewForDiscount) return;

		setShowDiscountModal(false);
		executePreview(pendingPreviewForDiscount, discountMonths);
		setPendingPreviewForDiscount(null);
	};

	const handleDiscountModalCancel = () => {
		setShowDiscountModal(false);
		setPendingPreviewForDiscount(null);
		setDiscountMonths(0);
	};

	const handleExecute = () => {
		setIsConfirmModalOpen(true);
	};

	const handleConfirmExecute = () => {
		if (!subscriptionQuery.data || !previewResult) return;

		const components = selectedAddOns.map(addOn => {
			const category = categorizeAddOn(addOn);
			// For additional users, send total (existing + new), not just the delta
			const quantity = category === 'additionalUsers' ? existingAdditionalUsersCount + additionalUsersQty : 1;
			return {
				componentId: addOn.componentId,
				pricePointId: addOn.pricePointId,
				quantity,
			};
		});

		executeMutation.mutate(
			{
				subscriptionId: subscriptionQuery.data.subscriptionId,
				accountId,
				components,
				newBillingCycle: billingCycle,
				useSplitBilling: previewResult.useSplitBilling ?? isSplitBilling,
				discountMonths,
			},
			{
				onSuccess: result => {
					setIsConfirmModalOpen(false);
					setChargedAmount(result.totalCharged);
					setExecuteWarnings(result.warnings ?? []);
					setServiceCreditApplied((result.serviceCreditAppliedInCents ?? 0) / 100);
					setIsSuccessModalOpen(true);
					// Reset form
					setSelectedAddOnIds(new Set());
					setAdditionalUsersQty(1);
					setBillingCycle(null);
					setIsSplitBilling(false);
					setDiscountMonths(0);
					setPreviewResult(null);

					// Fetch proforma invoice URL for the success modal
					setProformaInvoiceUrl(null);
					const customerId = billingReferenceId;
					if (customerId) {
						proformaInvoiceMutation.mutate(customerId, {
							onSuccess: url => {
								setProformaInvoiceUrl(url);
							},
							onError: error => {
								console.error('Failed to get proforma invoice URL:', error);
							},
						});
					}
				},
				onError: error => {
					setIsConfirmModalOpen(false);
					toast.error(`Failed to execute upgrade: ${error.message}`);
				},
			}
		);
	};

	const formatCurrency = (value: number | undefined): string => {
		const numericValue = value ?? 0;
		const hasCents = numericValue % 1 !== 0;
		return numericValue.toLocaleString('en-US', {
			maximumFractionDigits: hasCents ? 2 : 0,
			minimumFractionDigits: hasCents ? 2 : 0,
		});
	};

	const formatDate = (dateString: string | undefined): string => {
		if (!dateString) return 'N/A';
		const date = new Date(dateString);
		return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
	};

	const isLoading = subscriptionQuery.isLoading || addOnsQuery.isLoading;

	if (isLoading) {
		return (
			<div className={css(styleSheet.loadingContainer)}>
				<LoadingSpinner type='small' />
				<span>Loading subscription data...</span>
			</div>
		);
	}

	if (subscriptionQuery.isError || !subscriptionQuery.data) {
		return (
			<div className={css(styleSheet.loadingContainer)}>
				<div className={css(styleSheet.errorMessage)}>
					Failed to load subscription data. This account may not have an active subscription.
				</div>
			</div>
		);
	}

	const subscription = subscriptionQuery.data;

	// Check if any add-on in a category is selected
	const isCategorySelected = (category: AddOnCategory): boolean => {
		return groupedAddOns[category].some(addOn => selectedAddOnIds.has(addOn.id));
	};

	return (
		<div className={`${css(styleSheet.container, ...(customStyles || []))} ${className || ''}`}>
			<div className={css(styleSheet.layout)}>
				{/* Left: Form Inputs */}
				<form className={css(styleSheet.formSection)} onSubmit={handlePreview}>
					{/* Current Subscription Info */}
					<div className={css(styleSheet.sectionTitle)}>Current Subscription</div>
					<div className={css(styleSheet.currentSubscriptionCard)}>
						<div className={css(styleSheet.subscriptionRow)}>
							<span className={css(styleSheet.subscriptionLabel)}>Product</span>
							<span className={css(styleSheet.subscriptionValue)}>{subscription.productName}</span>
						</div>
						<div className={css(styleSheet.subscriptionRow)}>
							<span className={css(styleSheet.subscriptionLabel)}>Billing Cycle</span>
							<span className={css(styleSheet.subscriptionValue)}>{subscription.billingCycleLabel}</span>
						</div>
						<div className={css(styleSheet.subscriptionRow)}>
							<span className={css(styleSheet.subscriptionLabel)}>Next Billing Date</span>
							<span className={css(styleSheet.subscriptionValue)}>{formatDate(subscription.currentPeriodEndsAt)}</span>
						</div>
						<div className={css(styleSheet.subscriptionRow)}>
							<span className={css(styleSheet.subscriptionLabel)}>Days Remaining</span>
							<span className={css(styleSheet.subscriptionValue)}>{subscription.daysRemainingInPeriod} days</span>
						</div>
						{allocatedComponents.length > 0 && (
							<div className={css(styleSheet.componentsSection)}>
								<span className={css(styleSheet.subscriptionLabel)}>Components</span>
								<div className={css(styleSheet.componentsList)}>
									{allocatedComponents.map(component => {
										const isUserComponent = component.name.toLowerCase().includes('user');
										const showQuantity = isUserComponent || component.allocatedQuantity > 1;
										return (
											<div key={component.componentId} className={css(styleSheet.componentItem)}>
												{component.name}
												{showQuantity && ` (${component.allocatedQuantity})`}
											</div>
										);
									})}
								</div>
							</div>
						)}
					</div>

					{/* Billing Cycle Section */}
					{subscription.isEligibleForBillingCycleChange ? (
						<div className={css(styleSheet.formGroup)}>
							<label className={css(styleSheet.label)}>Billing Cycle</label>
							<Select value={billingCycle ?? ''} onValueChange={handleBillingCycleChange}>
								<SelectTrigger>
									<SelectValue placeholder='Keep Current'>{billingCycle ?? 'Keep Current'}</SelectValue>
								</SelectTrigger>
								<SelectContent>
									{billingCycleOptions.map(option => (
										<SelectItem key={option.value ?? 'null'} value={option.value ?? ''}>
											{option.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							{/* Inline info box for Annual → Quarterly change */}
							{billingCycle === 'Quarterly' && subscription.billingCycleMonths === 12 && (
								<div className={css(styleSheet.billingCycleInfoBox)}>
									Your billing cycle will change to Quarterly on {formatDate(subscription.currentPeriodEndsAt)}. Any
									add-ons selected will be charged pro-rated for the remaining period.
								</div>
							)}
						</div>
					) : (
						<div className={css(styleSheet.formGroup)}>
							<div className={css(styleSheet.infoBox)}>
								<div className={css(styleSheet.infoBoxTitle)}>Billing cycle changes not available</div>
								{subscription.billingCycleChangeBlockers?.length > 0 && (
									<ul className={css(styleSheet.infoBoxList)}>
										{subscription.billingCycleChangeBlockers.map((reason, i) => (
											<li key={i}>{reason}</li>
										))}
									</ul>
								)}
								<p style={{ margin: 0 }}>Please contact the Finance team for assistance.</p>
							</div>
						</div>
					)}

					{/* Split Billing Checkbox */}
					{showSplitBilling && (
						<div className={css(styleSheet.formGroup)}>
							<label className={css(styleSheet.checkboxLabel)}>
								<Checkbox checked={isSplitBilling} onCheckedChange={checked => setIsSplitBilling(checked === true)} />
								<span style={{ marginLeft: 8 }}>Split Billing (2 payments)</span>
							</label>
						</div>
					)}

					{/* Add-ons Section */}
					<div className={css(styleSheet.sectionTitle)}>Add Components</div>
					<div className={css(styleSheet.formGroup)}>
						{CATEGORY_ORDER.map(category => {
							const addOns = groupedAddOns[category];
							if (addOns.length === 0) return null;

							const isOwned = ownedCategories.has(category);

							return (
								<div key={category} style={{ marginBottom: 16 }}>
									<div className={css(styleSheet.label)}>{CATEGORY_LABELS[category]}</div>
									{isOwned && <div className={css(styleSheet.componentAlreadyOwned)}>Already included</div>}
									{!isOwned && (
										<div className={css(styleSheet.checkboxGrid)}>
											{addOns.map(addOn => {
												const isCustomContentDisabled =
													customContentEnabled && addOn.name.toLowerCase().includes('custom content');
												return (
													<div key={addOn.id}>
														<label
															className={css(styleSheet.checkboxLabel)}
															style={isCustomContentDisabled ? { opacity: 0.6 } : undefined}
														>
															<Checkbox
																checked={selectedAddOnIds.has(addOn.id)}
																disabled={isCustomContentDisabled}
																onCheckedChange={checked => handleAddOnToggle(addOn.id, checked === true)}
															/>
															<span>
																{addOn.name} - ${formatCurrency(addOn.unitPrice)}
																{isCustomContentDisabled && ' (Already enabled)'}
															</span>
														</label>
													</div>
												);
											})}
										</div>
									)}
									{/* Show quantity selector for Additional Users */}
									{category === 'additionalUsers' && isCategorySelected('additionalUsers') && (
										<div className={css(styleSheet.quantityContainer)}>
											<span>Quantity:</span>
											<input
												type='number'
												className={css(styleSheet.quantityInput)}
												value={additionalUsersQty}
												onChange={e => setAdditionalUsersQty(Math.max(1, parseInt(e.target.value) || 1))}
												min={1}
											/>
										</div>
									)}
								</div>
							);
						})}
						{filteredAddOns.length === 0 && (
							<div className={css(styleSheet.placeholder)}>No add-ons available for this billing cycle.</div>
						)}
					</div>

					<div className={css(styleSheet.buttonRow)}>
						<Button
							disabled={previewMutation.isPending || (selectedAddOns.length === 0 && billingCycle === null)}
							isLoading={previewMutation.isPending}
							type='submit'
							variant='primary'
						>
							Preview Upgrade
						</Button>
					</div>
				</form>

				{/* Right: Results Display */}
				<div className={css(styleSheet.resultsSection)}>
					{previewMutation.isPending && (
						<div className={css(styleSheet.spinnerContainer)}>
							<LoadingSpinner type='small' />
						</div>
					)}

					{!previewMutation.isPending && !previewResult && (
						<div className={css(styleSheet.resultsContent)}>
							<div className={css(styleSheet.placeholder)}>
								Select upgrade options and click <em>&quot;Preview Upgrade&quot;</em> to see pricing
							</div>
						</div>
					)}

					{!previewMutation.isPending && previewResult && (
						<div className={css(styleSheet.resultsContent)}>
							<div className={css(styleSheet.previewTitle)}>Upgrade Preview</div>

							{/* Scheduled Product Change Message */}
							{previewResult.scheduledProductChange && !previewResult.isImmediateBillingCycleChange && (
								<div className={css(styleSheet.scheduledChangeMessage)}>{previewResult.scheduledProductChange}</div>
							)}

							{/* Immediate Billing Cycle Change Section */}
							{previewResult.isImmediateBillingCycleChange && (
								<div className={css(styleSheet.immediateChangeSection)}>
									<div className={css(styleSheet.immediateChangeSectionTitle)}>Immediate Billing Cycle Change</div>
									<div className={css(styleSheet.resultRow)}>
										<span className={css(styleSheet.resultLabel)}>
											Credit for unused period
											<br />
											<span className={css(styleSheet.remainingCreditNote)}>
												({previewResult.daysRemainingInPeriod} days remaining)
											</span>
										</span>
										<span className={css(styleSheet.resultValue, styleSheet.serviceCreditRow)}>
											-${formatCurrency(previewResult.serviceCredit)}
										</span>
									</div>
									<div className={css(styleSheet.resultRow)}>
										<span className={css(styleSheet.resultLabel)}>New billing cycle charges</span>
										<span className={css(styleSheet.resultValue)}>
											${formatCurrency(previewResult.newBillingCycleCharges)}
										</span>
									</div>
								</div>
							)}

							{/* Selected Add-ons Summary - only show if not immediate billing cycle change */}
							{selectedAddOns.length > 0 && !previewResult.isImmediateBillingCycleChange && (
								<div className={css(styleSheet.breakdown)}>
									{selectedAddOns.map(addOn => {
										const category = categorizeAddOn(addOn);
										const quantity = category === 'additionalUsers' ? additionalUsersQty : 1;
										return (
											<div className={css(styleSheet.resultRow)} key={addOn.id}>
												<span className={css(styleSheet.resultLabel)}>
													{addOn.name}
													{quantity > 1 ? ` (x${quantity})` : ''}
												</span>
												<span className={css(styleSheet.resultValue)}>
													${formatCurrency(addOn.unitPrice * quantity)}
												</span>
											</div>
										);
									})}
								</div>
							)}

							{/* Component Warnings from API response */}
							{previewResult.components?.some(c => c.warning) && (
								<div className={css(styleSheet.breakdown)}>
									{previewResult.components
										.filter(component => component.warning)
										.map((component, index) => {
											const isUserComponent = component.componentName?.toLowerCase().includes('user');
											const newUserTotal = existingAdditionalUsersCount + additionalUsersQty;
											return (
												<div key={index} style={{ color: '#856404', fontSize: 12, marginBottom: 8 }}>
													Warning: {component.warning}
													{isUserComponent && (
														<div style={{ marginTop: 4 }}>
															After this upgrade, they will have a total of {newUserTotal} additional user
															{newUserTotal !== 1 ? 's' : ''}.
														</div>
													)}
												</div>
											);
										})}
								</div>
							)}

							{/* Pricing Breakdown - only show if not immediate billing cycle change */}
							{!previewResult.isImmediateBillingCycleChange && (
								<div className={css(styleSheet.breakdown)}>
									{/* Subtotal (pro-rated) */}
									<div className={css(styleSheet.resultRow)}>
										<span className={css(styleSheet.resultLabel)}>
											{previewResult.expectedDaysInPeriod
												? `Subtotal (pro-rated for ${previewResult.daysRemainingInPeriod} of ${previewResult.expectedDaysInPeriod} days)`
												: 'Subtotal (pro-rated)'}
										</span>
										<span className={css(styleSheet.resultValue)}>
											${formatCurrency(previewResult.totalProratedCharge)}
										</span>
									</div>

									{/* Discount row - only show if there's a discount */}
									{previewResult.totalDiscount != null && previewResult.totalDiscount > 0 && (
										<div className={css(styleSheet.resultRow)}>
											<span className={css(styleSheet.resultLabel)}>Discount</span>
											<span className={css(styleSheet.resultValue)} style={{ color: '#155724' }}>
												-${formatCurrency(previewResult.totalDiscount)}
											</span>
										</div>
									)}
								</div>
							)}

							{/* Total Due Today */}
							<div className={css(styleSheet.highlightedRow)}>
								<div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
									<span className={css(styleSheet.highlightedLabel)}>
										{previewResult.isImmediateBillingCycleChange
											? 'Amount Due Today'
											: previewResult.useSplitBilling
												? 'Total Due'
												: 'Total Due Today'}
									</span>
									<span className={css(styleSheet.highlightedValue)}>
										$
										{formatCurrency(
											previewResult.isImmediateBillingCycleChange
												? previewResult.amountDueToday
												: (previewResult.netTotal ?? previewResult.totalProratedCharge)
										)}
									</span>
								</div>
								{/* Split Billing Note */}
								{previewResult.useSplitBilling && previewResult.splitBillingFirstPayment != null && (
									<div className={css(styleSheet.splitBillingNote)}>
										Split Billing: ${formatCurrency(previewResult.splitBillingFirstPayment)} due today, $
										{formatCurrency(previewResult.splitBillingSecondPayment)} due in 60 days
									</div>
								)}
							</div>

							{/* Remaining Credit for Immediate Changes */}
							{previewResult.isImmediateBillingCycleChange && previewResult.remainingCredit > 0 && (
								<div className={css(styleSheet.summaryRow)}>
									<div className={css(styleSheet.summaryCard)}>
										<div>
											<strong>Remaining Credit:</strong> ${formatCurrency(previewResult.remainingCredit)}
										</div>
										<div className={css(styleSheet.remainingCreditNote)}>
											This credit will be applied to future invoices.
										</div>
									</div>
								</div>
							)}

							{/* Service Credit Note (surplus discount applied as credit on next invoice) */}
							{!previewResult.isImmediateBillingCycleChange && previewResult.serviceCreditInCents > 0 && (
								<div className={css(styleSheet.serviceCreditNote)}>
									${formatCurrency(previewResult.serviceCreditInCents / 100)} will be applied as a service credit to the
									customer&apos;s next invoice.
								</div>
							)}

							{/* Summary Info */}
							<div className={css(styleSheet.summaryRow)}>
								<div className={css(styleSheet.summaryCard)}>
									<div>
										<strong>Next Billing Date:</strong> {formatDate(previewResult.currentState?.currentPeriodEndsAt)}
									</div>
									<div>
										<strong>Days Remaining:</strong> {previewResult.daysRemainingInPeriod} days
									</div>
									{previewResult.totalTaxInCents > 0 && (
										<div>
											<strong>Estimated Tax:</strong> ${formatCurrency(previewResult.totalTaxInCents / 100)}
										</div>
									)}
								</div>
							</div>

							{/* Next Invoice Preview Link */}
							<div
								className={css(styleSheet.nextInvoiceLink)}
								onClick={() => {
									if (!subscriptionQuery.data) return;
									const components = selectedAddOns.map(addOn => {
										const category = categorizeAddOn(addOn);
										const quantity =
											category === 'additionalUsers' ? existingAdditionalUsersCount + additionalUsersQty : 1;
										return {
											componentId: addOn.componentId,
											pricePointId: addOn.pricePointId,
											quantity,
										};
									});
									setIsNextInvoiceModalOpen(true);
									nextInvoiceMutation.mutate({
										subscriptionId: subscriptionQuery.data.subscriptionId,
										accountId,
										components,
										newBillingCycle: billingCycle,
										useSplitBilling: isSplitBilling,
										discountMonths,
									});
								}}
								role='button'
								tabIndex={0}
								onKeyDown={e => {
									if (e.key === 'Enter' || e.key === ' ') {
										e.currentTarget.click();
									}
								}}
							>
								Preview next full invoice amount (estimate) →
							</div>

							{/* Action Buttons */}
							<div className={css(styleSheet.actionButtons)}>
								<Button
									onClick={handleExecute}
									disabled={
										executeMutation.isPending ||
										(previewResult.components?.some(c => c.warning && c.proratedCharge === 0) ?? false)
									}
									isLoading={executeMutation.isPending}
									type='button'
									variant='success'
								>
									Upgrade Account Now!
								</Button>
								<Button onClick={() => setIsUpgradeProposalModalOpen(true)} type='button' variant='secondary'>
									Generate Upgrade Proposal
								</Button>
								<Button onClick={() => setIsCurrentClientContractOpen(true)} type='button' variant='secondary'>
									Current Client Contract
								</Button>
							</div>
						</div>
					)}
				</div>
			</div>

			{/* Confirmation Modal */}
			<Modal isOpen={isConfirmModalOpen} onRequestClose={() => setIsConfirmModalOpen(false)} useDefaultHeader={true}>
				<div className={css(styleSheet.confirmModal)}>
					<h2 className={css(styleSheet.confirmTitle)}>Confirm Upgrade</h2>
					<p className={css(styleSheet.confirmMessage)}>
						Are you sure you want to execute this upgrade?
						{previewResult?.useSplitBilling ? ' The customer will be charged today:' : ' The customer will be charged:'}
					</p>
					<div className={css(styleSheet.confirmAmount)}>
						$
						{formatCurrency(
							previewResult?.useSplitBilling && previewResult.splitBillingFirstPayment != null
								? previewResult.splitBillingFirstPayment
								: previewResult?.isImmediateBillingCycleChange
									? previewResult.amountDueToday
									: (previewResult?.netTotal ?? previewResult?.totalProratedCharge)
						)}
					</div>
					{previewResult?.useSplitBilling && previewResult.splitBillingSecondPayment != null && (
						<div style={{ color: '#155724', fontSize: 14, marginTop: 4 }}>
							${formatCurrency(previewResult.splitBillingSecondPayment)} will be charged in 60 days
						</div>
					)}
					{previewResult?.isImmediateBillingCycleChange && previewResult.serviceCredit > 0 && (
						<div style={{ color: '#155724', fontSize: 14, marginTop: 4 }}>
							(includes ${formatCurrency(previewResult.serviceCredit)} service credit)
						</div>
					)}
					{!previewResult?.isImmediateBillingCycleChange &&
						!previewResult?.useSplitBilling &&
						previewResult?.totalDiscount != null &&
						previewResult.totalDiscount > 0 && (
							<div style={{ color: '#155724', fontSize: 14, marginTop: 4 }}>
								(includes ${formatCurrency(previewResult.totalDiscount)} discount)
							</div>
						)}
					<div className={css(styleSheet.confirmButtons)}>
						<Button variant='secondary' onClick={() => setIsConfirmModalOpen(false)}>
							Cancel
						</Button>
						<Button
							variant='success'
							onClick={handleConfirmExecute}
							isLoading={executeMutation.isPending}
							disabled={executeMutation.isPending}
						>
							Confirm & Charge
						</Button>
					</div>
				</div>
			</Modal>

			{/* Success Modal */}
			<Modal isOpen={isSuccessModalOpen} onRequestClose={() => setIsSuccessModalOpen(false)} useDefaultHeader={true}>
				<div className={css(styleSheet.successModal)}>
					<h2 className={css(styleSheet.successTitle)}>Upgrade Successful!</h2>
					{executeWarnings.length > 0 &&
						executeWarnings.map((warning, index) => (
							<div key={`${index}-${warning}`} className={css(styleSheet.warningBanner)}>
								{warning}
							</div>
						))}
					<p className={css(styleSheet.successMessage)}>
						The upgrade has been applied successfully. The customer was charged ${formatCurrency(chargedAmount)}.
					</p>
					{serviceCreditApplied > 0 && (
						<p className={css(styleSheet.successMessage)}>
							${formatCurrency(serviceCreditApplied)} service credit was applied to the customer&apos;s next invoice.
						</p>
					)}
					{proformaInvoiceUrl && (
						<p className={css(styleSheet.successMessage)}>
							<a
								href={proformaInvoiceUrl}
								target='_blank'
								rel='noopener noreferrer'
								className={css(styleSheet.invoiceLink)}
							>
								Preview Next Invoice →
							</a>
						</p>
					)}
					{proformaInvoiceMutation.isPending && (
						<p className={css(styleSheet.successMessage)} style={{ color: '#6c757d', fontSize: 13 }}>
							Loading invoice preview link...
						</p>
					)}
					{proformaInvoiceMutation.isError && (
						<p className={css(styleSheet.successMessage)} style={{ color: '#dc3545', fontSize: 13 }}>
							Could not load invoice preview link.
						</p>
					)}
					<Button variant='primary' onClick={() => setIsSuccessModalOpen(false)}>
						Close
					</Button>
				</div>
			</Modal>

			{/* Generate Upgrade Proposal Modal */}
			<GenerateUpgradeProposalModal
				customContentEnabled={customContentEnabled}
				isOpen={isUpgradeProposalModalOpen}
				onRequestClose={() => setIsUpgradeProposalModalOpen(false)}
				prefillData={upgradeProposalPrefillData}
			/>

			{/* Current Client Contract Modal */}
			<GenerateContractModal
				isOpen={isCurrentClientContractOpen}
				onRequestClose={() => setIsCurrentClientContractOpen(false)}
				contractType='current'
				prefillData={contractPrefillData}
				isSuccessTeam={isSuccessTeam}
			/>

			{/* Next Invoice Preview Modal */}
			<Modal
				isOpen={isNextInvoiceModalOpen}
				onRequestClose={() => setIsNextInvoiceModalOpen(false)}
				useDefaultHeader={true}
			>
				<div className={css(styleSheet.nextInvoiceModal)}>
					<div className={css(styleSheet.nextInvoiceModalTitle)}>Estimated Next Invoice</div>
					{nextInvoiceMutation.data && (
						<div className={css(styleSheet.nextInvoiceModalSubtitle)}>
							{nextInvoiceMutation.data.nextInvoiceBillingCycleLabel} — Next invoice:{' '}
							{formatDate(nextInvoiceMutation.data.nextInvoiceDate)}
						</div>
					)}

					{nextInvoiceMutation.isPending && (
						<div className={css(styleSheet.spinnerContainer)} style={{ padding: 40 }}>
							<LoadingSpinner type='small' />
						</div>
					)}

					{nextInvoiceMutation.isError && (
						<div className={css(styleSheet.errorMessage)}>
							Failed to load next invoice preview: {nextInvoiceMutation.error?.message}
						</div>
					)}

					{nextInvoiceMutation.data && (
						<>
							<table className={css(styleSheet.nextInvoiceTable)}>
								<thead>
									<tr className={css(styleSheet.nextInvoiceRow)}>
										<th className={css(styleSheet.nextInvoiceCell)} style={{ textAlign: 'left' }}>
											Item
										</th>
										<th className={css(styleSheet.nextInvoiceCellRight)}>Amount</th>
									</tr>
								</thead>
								<tbody>
									{nextInvoiceMutation.data.lineItems.map((item, index) => (
										<tr key={index} className={css(styleSheet.nextInvoiceRow)}>
											<td className={css(styleSheet.nextInvoiceCell)}>
												<span style={item.isNewFromUpgrade ? { fontWeight: 600 } : undefined}>{item.name}</span>
												{item.isNewFromUpgrade && <span className={css(styleSheet.nextInvoiceNewBadge)}>NEW</span>}
												{item.memo && <div className={css(styleSheet.nextInvoiceMemo)}>{item.memo}</div>}
											</td>
											<td className={css(styleSheet.nextInvoiceCellRight)}>${formatCurrency(item.amount)}</td>
										</tr>
									))}
								</tbody>
							</table>

							{/* Summary */}
							<div className={css(styleSheet.nextInvoiceSummaryRow)}>
								<span>Subtotal</span>
								<span>${formatCurrency(nextInvoiceMutation.data.subtotal)}</span>
							</div>
							{nextInvoiceMutation.data.totalDiscount > 0 && (
								<div className={css(styleSheet.nextInvoiceSummaryRow)}>
									<span>Discount</span>
									<span style={{ color: '#155724' }}>-${formatCurrency(nextInvoiceMutation.data.totalDiscount)}</span>
								</div>
							)}
							{nextInvoiceMutation.data.totalTax > 0 && (
								<div className={css(styleSheet.nextInvoiceSummaryRow)}>
									<span>Tax</span>
									<span>${formatCurrency(nextInvoiceMutation.data.totalTax)}</span>
								</div>
							)}
							{previewResult && previewResult.serviceCreditInCents > 0 && (
								<div className={css(styleSheet.nextInvoiceSummaryRow, styleSheet.projectedCreditRow)}>
									<span>Service credit (applied after upgrade)</span>
									<span>-${formatCurrency(previewResult.serviceCreditInCents / 100)}</span>
								</div>
							)}
							{/* The next-invoice API is a Chargify passthrough that doesn't know about the pending
								service credit from the upgrade preview. We subtract it client-side so the sales rep
								sees the effective total. After upgrade execution, the credit exists in Chargify and
								re-fetching the invoice preview will include it natively. */}
							<div className={css(styleSheet.nextInvoiceTotal)}>
								<span>Estimated Total</span>
								<span>
									$
									{formatCurrency(
										previewResult && previewResult.serviceCreditInCents > 0
											? Math.max(0, nextInvoiceMutation.data.estimatedTotal - previewResult.serviceCreditInCents / 100)
											: nextInvoiceMutation.data.estimatedTotal
									)}
								</span>
							</div>

							{/* Caveats */}
							{nextInvoiceMutation.data.caveats.length > 0 &&
								nextInvoiceMutation.data.caveats.map((caveat, index) => (
									<div key={index} className={css(styleSheet.nextInvoiceCaveat)}>
										{caveat}
									</div>
								))}
						</>
					)}

					<div style={{ marginTop: 16, textAlign: 'center' }}>
						<Button variant='secondary' onClick={() => setIsNextInvoiceModalOpen(false)}>
							Close
						</Button>
					</div>
				</div>
			</Modal>

			{/* Discount Modal */}
			<Modal isOpen={showDiscountModal} onRequestClose={handleDiscountModalCancel} useDefaultHeader={true}>
				<div className={css(styleSheet.discountModal)}>
					<div className={css(styleSheet.discountModalTitle)}>Apply Promotional Discount?</div>
					<p className={css(styleSheet.discountModalDescription)}>
						The selected add-ons are eligible for a promotional discount.
					</p>

					{([0, 1, 2, 3] as const).map(months => (
						<div
							key={months}
							className={css(styleSheet.discountOption, discountMonths === months && styleSheet.discountOptionSelected)}
							onClick={() => setDiscountMonths(months)}
						>
							<div className={css(styleSheet.billingCycleOptionTitle)}>
								<div
									className={css(
										styleSheet.radioIndicator,
										discountMonths === months && styleSheet.radioIndicatorSelected
									)}
								>
									{discountMonths === months && <div className={css(styleSheet.radioIndicatorDot)} />}
								</div>
								{months === 0 ? 'No discount' : `${months} month${months > 1 ? 's' : ''} free`}
							</div>
							{months === 2 && (
								<div className={css(styleSheet.discountWarning)}>
									Only use if kickoff is more than <strong>30 days</strong> from now
								</div>
							)}
							{months === 3 && (
								<div className={css(styleSheet.discountWarning)}>
									Only use if kickoff is more than <strong>45 days</strong> from now
								</div>
							)}
						</div>
					))}

					<div className={css(styleSheet.discountModalButtons)}>
						<Button variant='secondary' onClick={handleDiscountModalCancel}>
							Cancel
						</Button>
						<Button variant='primary' onClick={handleDiscountModalConfirm}>
							Continue to Preview
						</Button>
					</div>
				</div>
			</Modal>
		</div>
	);
}
